import * as React from 'react';
import { observer } from 'mobx-react';
import { PagePaymentMethodViewModel } from './page-payment-method-view-model';
import { PagePaymentMethodSupportedDescription } from './page-payment-method-supported-description';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { SubmitButton } from '../../components/submit-button/submit-button';
import { Form } from '../../primitives/form';
import { ErrorMessage } from '../../components/error-message/error-message';
import { CardPaymentMethod } from './card-payment-method';
import { Tabs } from '../../components/tabs/tabs';
import { PaymentMethodType } from '../../webgiving-generated';
import { AchPaymentMethod } from './ach-payment-method';
import { FadeTransition } from '../../components/fade-transition';
import { PanelContentTransition } from '../../components/panel-content-transition';
import { getPageClasses} from '../helpers/get-page-classes';
import { PanelHeading } from '../../components/panel-heading';
import { Text } from '../../components/text';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { PanelFooter } from '../../components/panel-footer';

import { panelHeading, panelHeadingContent, panelHeadingWithTabs, tabs, transitionDurationMs } from './page-payment-method.less';
import { pageFooter } from '../../main/webgiving-main.less';
import { LeftChevronLink } from '../../primitives/link/link';

export { PagePaymentMethodViewModel };

@injectWebGivingProps
@observer
export class PagePaymentMethod extends React.Component<{
	vm: PagePaymentMethodViewModel,
	transitionState: string,
} & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const {
			error,
			paymentMethods,
			paymentMethodType,
			switchPaymentMethodType,
			merchant,
			achModel,
			cardModel,
			merchantSupportsCard,
			isLoading,
			showBackToConfirmGift,
			pageShouldBeVisible,
			isBlocked,
			hideACH
		} = vm;
		const isCard = paymentMethodType === PaymentMethodType.CreditCard;
		const isAch = paymentMethodType === PaymentMethodType.ACH;
		const transitionProps = {
			exit: false,
			timeout: 0,
			duration: parseInt(transitionDurationMs, 10),
		};

		const shouldHideACH = hideACH;
		const multiplePaymentMethods = shouldHideACH ? false : paymentMethods.length > 1;
		const elementsShouldEnter = transitionState === 'entered';

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading)} data-pp-at-target={pageShouldBeVisible ? `Payment Method page` : null}>
				<PanelHeading vm={vm} className={`${panelHeading} ${multiplePaymentMethods ? panelHeadingWithTabs : ''}`}>
					<FadeTransition in={elementsShouldEnter}>
						<div className={panelHeadingContent}>
							<h1 className="panel-title"><Text>PAYMENT METHOD</Text></h1>
							<p className="panel-subtitle">
								<PagePaymentMethodSupportedDescription merchant={merchant} supportsCard={merchantSupportsCard} />
							</p>
							{showBackToConfirmGift && (
								<LeftChevronLink acceptanceTestTargetId="Change your number" onClick={this.handleBackToConfirmGift} branded={false}>
									<Text>Go back to confirm {merchant.PaymentLabel.NounLowerCase}</Text>
								</LeftChevronLink>
							)}
							{ multiplePaymentMethods && (
								<div className={tabs}>
									<Tabs options={paymentMethods} selectedTab={paymentMethodType} onChange={switchPaymentMethodType} />
								</div>
							)}
						</div>
					</FadeTransition>
				</PanelHeading>
				<Form onSubmit={this.handleSubmit}>
					<div className="panel-body panel-body-form" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage error={error} />
								{ merchantSupportsCard && (
									<FadeTransition in={isCard} {...transitionProps}>
										<div>
											<CardPaymentMethod vm={cardModel} />
										</div>
									</FadeTransition>
								)}
								{ !shouldHideACH && merchant.SupportsAch && (
									<FadeTransition in={isAch} {...transitionProps}>
										<div>
											<AchPaymentMethod vm={achModel} />
										</div>
									</FadeTransition>
								)}
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={pageFooter}>
						<SubmitButton acceptanceTestTargetId="Next" isLoading={isLoading} isBlocked={isBlocked}><Text>Next</Text></SubmitButton>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}

	private handleBackToConfirmGift = (event: React.MouseEvent<HTMLAnchorElement>) => {
		raiseAction(new WebGivingUserAction.BackToConfirmGift(), this.props);
	}
}
